import React from 'react';

interface ShareButtonsProps {
  title: string;
  url: string;
  compact?: boolean;
}

const ShareButtons: React.FC<ShareButtonsProps> = ({ title, url, compact = false }) => {
  const encodedTitle = encodeURIComponent(title);
  
  // Convert blog URL to static HTML URL for social sharing
  let shareUrl = url;
  if (url.includes('/blog/')) {
    const slug = url.split('/blog/')[1];
    // Use production domain for social sharing
    shareUrl = `https://lendefimarkets.com/social/blog/${slug}.html`;
  }
  const encodedUrl = encodeURIComponent(shareUrl);
  
  const shareLinks = [
    {
      name: 'X (Twitter)',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
      url: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      color: '#1DA1F2'
    },
    {
      name: 'Reddit',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
        </svg>
      ),
      url: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`,
      color: '#FF4500'
    },
    {
      name: 'Medium',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M13.54 12a6.8 6.8 0 01-6.77 6.82A6.8 6.8 0 010 12a6.8 6.8 0 016.77-6.82A6.8 6.8 0 0113.54 12zM20.96 12c0 3.54-1.51 6.42-3.38 6.42-1.87 0-3.39-2.88-3.39-6.42s1.52-6.42 3.39-6.42 3.38 2.88 3.38 6.42M24 12c0 3.17-.53 5.75-1.19 5.75-.66 0-1.19-2.58-1.19-5.75s.53-5.75 1.19-5.75S24 8.83 24 12z"/>
        </svg>
      ),
      url: `https://medium.com/new-story?url=${encodedUrl}&title=${encodedTitle}`,
      color: '#00AB6C'
    },
    {
      name: 'Substack',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z"/>
        </svg>
      ),
      url: `https://substack.com/notes`,
      color: '#FF6719'
    },
    {
      name: 'Farcaster',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.24 3.98h-1.92V2.06c0-.31.25-.56.56-.56h.8c.31 0 .56.25.56.56v1.92zM7.68 3.98H5.76V2.06c0-.31.25-.56.56-.56h.8c.31 0 .56.25.56.56v1.92zM5.76 21.5h1.92V9.86c0-1.77 1.43-3.2 3.2-3.2h2.24c1.77 0 3.2 1.43 3.2 3.2V21.5h1.92V3.98H5.76V21.5z"/>
        </svg>
      ),
      url: `https://farcaster.xyz/~/compose?text=${encodedTitle}&embeds[]=${encodedUrl}`,
      color: '#8A63D2'
    }
  ];

  if (compact) {
    return (
      <div className="share-buttons-compact">
        {shareLinks.map((link) => (
          <a
            key={link.name}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="share-icon"
            title={`Share on ${link.name}`}
            style={{ color: link.color }}
          >
            {link.icon}
          </a>
        ))}
      </div>
    );
  }

  return (
    <div className="share-buttons">
      {shareLinks.map((link) => (
        <a
          key={link.name}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="share-button"
          style={{ borderColor: link.color, color: link.color }}
        >
          {link.icon}
          <span>Share on {link.name}</span>
        </a>
      ))}
    </div>
  );
};

export default ShareButtons;